import { useMutation } from '@tanstack/react-query'
import { queryClient } from '@core/Data/ReactQuery/queryClient'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { triggerHapticFeedback } from '@core/Utils/hapticFeedback/hapticFeedback'
import type { FollowMeSharedBet } from '@core/Areas/FollowMe/types'
import { handleApiErrorNotification } from '@core/Areas/FollowMe/helpers/handleApiErrorNotification'
import {
  postFollowMeFollowAccount,
  postFollowMeUnfollowAccount,
} from '@core/Areas/FollowMe/helpers/api'
import { trackBetLoopToggleFollow } from '@core/Areas/FollowMe/helpers/analytics'
import {
  patchFeedDataWithIsFollowingChange,
  patchPrincipalDataWithFollowingCount,
} from '../helpers/patchReactQueryData'

export const useFollowMutation = ({
  profileId,
  onFollowSuccess,
  isFollowingLocalState,
}: {
  isFollowingLocalState: boolean
  profileId: FollowMeSharedBet['ownerAccountId']
  onFollowSuccess(newState: boolean): void
}) => {
  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      isFollowingLocalState
        ? postFollowMeUnfollowAccount(profileId)
        : postFollowMeFollowAccount(profileId),
    onSuccess: () => {
      const newIsFollowingState = !isFollowingLocalState
      triggerHapticFeedback('impact-light')
      onFollowSuccess(newIsFollowingState)
      patchFeedDataWithIsFollowingChange({ profileId, isFollowing: newIsFollowingState })
      patchPrincipalDataWithFollowingCount(newIsFollowingState)
      trackBetLoopToggleFollow(newIsFollowingState ? 'enable' : 'disable')
      queryClient.invalidateQueries({ queryKey: queryKeys.followMeAccountProfileBase })
    },
    onError: e => handleApiErrorNotification(e),
  })

  return { mutate, isPending }
}
